import { useEffect, useRef, useState } from "react";
import api, { eur } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Wallet, Sparkles, Upload, FileText, AlertTriangle, Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const itDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const SubHead = ({ children }) => (
  <h3 className="mb-1 mt-6 font-mono text-[11px] font-bold uppercase tracking-widest text-primary first:mt-2">{children}</h3>
);

const FidoBox = ({ titolo, sug, onApply, applying }) => {
  if (!sug) return null;
  const isCs = titolo === "creditsafe";
  const bad = !!sug.non_affidabile;
  const label = bad ? "NON AFFIDABILE" : (sug.rinnovo_obbligatorio ? "rinnovo obbligatorio" : (sug.applicabile && sug.fido_auto != null ? eur(sug.fido_auto) : "verifica manuale"));
  return (
    <div className={`rounded-lg border p-3 ${bad ? "border-red-500/60 bg-red-500/10" : "border-primary/30 bg-primary/5"}`} data-testid={`fido-box-${titolo}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className={`text-[11px] font-bold uppercase tracking-wide ${bad ? "text-red-500" : "text-primary"}`}>{isCs ? "Da Creditsafe" : "Da bilancio"}</span>
          <div className={`font-heading text-lg font-bold ${bad ? "text-red-600 dark:text-red-400" : ""}`}>{label}</div>
        </div>
        {sug.applicabile && sug.fido_auto != null && (
          <Button size="sm" variant="outline" onClick={() => onApply(sug.fido_auto)} disabled={applying} data-testid={`fido-applica-${titolo}`}>Applica</Button>
        )}
      </div>
      {isCs ? (
        <>
          {(sug.rischio || sug.score != null) && <p className="mt-1 text-[11px] font-semibold">Valutazione: {sug.rischio || "—"}{sug.score != null ? ` · score ${sug.score}` : ""}</p>}
          {sug.note && <p className="mt-1 text-[11px] text-muted-foreground">{sug.note}</p>}
          {sug.data_registrazione && (
            <p className={`mt-1 flex items-center gap-1 text-[11px] ${sug.troppo_vecchio ? "font-semibold text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}>
              {sug.troppo_vecchio && <AlertTriangle className="h-3 w-3 shrink-0" />}
              Dato registrato il {new Date(sug.data_registrazione).toLocaleDateString("it-IT")}{sug.eta_mesi != null ? ` · ${sug.eta_mesi} mesi fa` : ""}{sug.troppo_vecchio ? " — da aggiornare (oltre 6 mesi)" : ""}
            </p>
          )}
        </>
      ) : sug.breakdown ? (
        <p className="mt-1 text-[11px] text-muted-foreground">10% fatturato ({eur(sug.breakdown.base_fatturato)}) · tetto 50% patrimonio ({eur(sug.breakdown.tetto_patrimonio)}) · coeff. rischio {sug.breakdown.coeff_rischio} · max {eur(200000)}</p>
      ) : null}
    </div>
  );
};

export default function AffidamentoCard({ kind, id, doc, onRefresh }) {
  const base = kind === "prospect" ? "prospects" : "partners";
  const isPartner = kind !== "prospect";
  const p = doc || {};
  const fd = p.fier_dettaglio || {};
  const [affVal, setAffVal] = useState("");
  const [affSaving, setAffSaving] = useState(false);
  const [fidoSug, setFidoSug] = useState(null);
  const [csMan, setCsMan] = useState({ score: "", fido_consigliato: "", data_fondazione: "", dipendenti: "" });
  const [csManSaving, setCsManSaving] = useState(false);
  const [bilUploading, setBilUploading] = useState(false);
  const [csEnriching, setCsEnriching] = useState(false);
  const [payMethods, setPayMethods] = useState([]);
  const [payDefault, setPayDefault] = useState("__none__");
  const [paySaving, setPaySaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const bilFileRef = useRef(null);

  useEffect(() => { setAffVal(p.affidamento_max != null ? String(p.affidamento_max) : ""); }, [p.affidamento_max]);
  useEffect(() => { setPayDefault(p.modalita_pagamento || "__none__"); }, [p.modalita_pagamento]);
  useEffect(() => { const m = p.creditsafe_manual || {}; setCsMan({ score: m.score ?? "", fido_consigliato: m.fido_consigliato ?? "", data_fondazione: m.data_fondazione ?? "", dipendenti: m.dipendenti ?? "" }); }, [p.creditsafe_manual]);
  useEffect(() => { api.get("/payment-methods").then((r) => setPayMethods(r.data.methods || [])).catch(() => {}); }, []);
  useEffect(() => { api.get(`/${base}/${id}/fido-suggerito`).then((r) => setFidoSug(r.data)).catch(() => setFidoSug(null)); /* eslint-disable-next-line */ }, [id, p.bilancio, p.creditsafe_manual]);

  const saveAffidamento = async (value) => {
    setAffSaving(true);
    try { await api.put(`/${base}/${id}/affidamento`, { affidamento_max: value === "" ? null : value }); toast.success("Fido massimo aggiornato"); onRefresh?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setAffSaving(false); }
  };
  const applyFido = (value) => { setAffVal(String(value)); saveAffidamento(value); };
  const saveCsManual = async () => {
    setCsManSaving(true);
    try { await api.put(`/${base}/${id}/creditsafe-manual`, csMan); toast.success("Dati Creditsafe salvati"); onRefresh?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setCsManSaving(false); }
  };
  const verify = async () => {
    setVerifying(true);
    try { await api.post(`/${base}/${id}/affidamento-verifica`); toast.success("Verifica affidamento confermata"); onRefresh?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setVerifying(false); }
  };
  const savePayDefault = async (val) => {
    setPayDefault(val); setPaySaving(true);
    try { await api.put(`/${base}/${id}/pagamento-default`, { modalita: val === "__none__" ? "" : val }); toast.success("Modalità di pagamento di default salvata"); onRefresh?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setPaySaving(false); }
  };
  const uploadBilancio = async (file) => {
    if (!file) return;
    setBilUploading(true);
    try {
      const fdd = new FormData(); fdd.append("file", file);
      await api.post(`/${base}/${id}/bilancio-upload`, fdd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bilancio caricato e dati acquisiti automaticamente"); onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Caricamento non riuscito"); }
    finally { setBilUploading(false); }
  };
  const enrichCreditsafe = async () => {
    setCsEnriching(true);
    try { await api.post(`/${base}/${id}/creditsafe-enrich`); toast.success("Report Creditsafe aggiornato"); onRefresh?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Creditsafe non disponibile"); }
    finally { setCsEnriching(false); }
  };

  const b = p.bilancio || {};
  const personale = b.costo_personale != null
    ? <>{eur(b.costo_personale)} <span className="text-[11px] font-normal text-muted-foreground">(≈ {b.stima_dipendenti ?? Math.round(b.costo_personale / 40000)} pers.)</span></>
    : "—";
  const items = [
    ["Anno", b.anno ?? fd.anno_bilancio, "plain"], ["Fatturato", b.fatturato ?? fd.fatturato],
    ["Patrimonio netto", b.patrimonio], ["Magazzino", b.magazzino],
    ["Costo del personale", personale, "node"], ["Utile ante imposte", b.utile_ante_imposte],
    ["EBITDA (calcolato)", b.ebitda], ["PFN (calcolata)", b.pfn],
  ];

  return (
    <Card className="p-6" data-testid={`${kind}-affidamento-card`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2"><Wallet className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold tracking-tight">Fido & Ultimo bilancio</h2></div>
        {isPartner && (
          <Button variant="outline" size="sm" onClick={enrichCreditsafe} disabled={csEnriching} data-testid="cs-enrich-btn">
            {csEnriching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Arricchisci da Creditsafe
          </Button>
        )}
      </div>
      <div>
        <label className="text-xs font-semibold text-muted-foreground">Fido massimo concesso (€)</label>
        <div className="mt-1 flex max-w-md gap-2">
          <Input type="number" value={affVal} onChange={(e) => setAffVal(e.target.value)} data-testid="aff-input" placeholder={fd.fido_massimo != null ? `Gestionale: ${fd.fido_massimo}` : "—"} />
          <Button onClick={() => saveAffidamento(affVal)} disabled={affSaving} data-testid="aff-save">{affSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva</Button>
          <Button variant="outline" onClick={verify} disabled={verifying} data-testid="aff-verifica" title="Conferma la verifica dell'affidamento (azzera il timer 6 mesi)">{verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}</Button>
        </div>
        {p.affidamento_verificato_at && <p className="mt-1 text-[11px] text-muted-foreground">Ultima verifica: {itDate(p.affidamento_verificato_at)}{p.affidamento_updated_by ? ` · ${p.affidamento_updated_by}` : ""}</p>}

        <div className="mt-4 border-t border-border pt-4" data-testid={`${kind}-pagamento-default`}>
            <label className="text-xs font-semibold text-muted-foreground">Modalità di pagamento (default anagrafica)</label>
            <div className="mt-1 flex max-w-md items-center gap-2">
              <Select value={payDefault} onValueChange={savePayDefault} disabled={paySaving}>
                <SelectTrigger className="w-full" data-testid={`${kind}-pagamento-select`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Default globale (Ad avviso merce pronta) —</SelectItem>
                  {payMethods.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {paySaving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">L'agente nel preventivo la vedrà pre-selezionata (già approvata).{isPartner ? "" : " Se il prospect diventa cliente viene copiata nella sua anagrafica."}</p>
          </div>

        <div className="mt-3 space-y-2" data-testid="fido-suggeriti">
          <div className="text-xs font-semibold text-muted-foreground">Fido suggerito — puoi usare bilancio e/o Creditsafe</div>
          {(fidoSug?.bilancio || fidoSug?.creditsafe) ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <FidoBox titolo="bilancio" sug={fidoSug?.bilancio} onApply={applyFido} applying={affSaving} />
              <FidoBox titolo="creditsafe" sug={fidoSug?.creditsafe} onApply={applyFido} applying={affSaving} />
            </div>
          ) : (
            <div className="flex items-center gap-2 text-[11px] font-semibold text-amber-600 dark:text-amber-400" data-testid="fido-no-fonti">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Carica un bilancio PDF oppure inserisci i dati Creditsafe per calcolare il fido.
            </div>
          )}
          <div className="mt-2 rounded-lg border border-border bg-muted/30 p-3" data-testid="cs-manual-form">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Dati Creditsafe (inserimento manuale)</div>
            <div className="grid gap-2 sm:grid-cols-4">
              {[["score", "Score Creditsafe"], ["fido_consigliato", "Fido consigliato (€)"], ["data_fondazione", "Data di fondazione"], ["dipendenti", "Dipendenti"]].map(([k, lab]) => (
                <div key={k}>
                  <label className="text-[11px] font-semibold text-muted-foreground">{lab}</label>
                  <Input className="mt-1" type={k === "data_fondazione" ? "date" : "number"} value={csMan[k] ?? ""} onChange={(e) => setCsMan((s) => ({ ...s, [k]: e.target.value }))} data-testid={`cs-man-${k}`} />
                </div>
              ))}
            </div>
            <div className="mt-2 flex justify-end"><Button size="sm" onClick={saveCsManual} disabled={csManSaving} data-testid="cs-man-save">{csManSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva dati Creditsafe</Button></div>
          </div>
        </div>
      </div>

      <div className="mt-5 mb-2 flex flex-wrap items-center justify-between gap-2">
        <SubHead>Ultimo bilancio (acquisito automaticamente da PDF)</SubHead>
        <input ref={bilFileRef} type="file" accept="application/pdf" className="hidden" data-testid="bil-upload-input"
          onChange={(e) => { uploadBilancio(e.target.files?.[0]); e.target.value = ""; }} />
        <Button size="sm" disabled={bilUploading} onClick={() => bilFileRef.current?.click()} data-testid="bil-upload-btn">
          {bilUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Carica bilancio (PDF)
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-3" data-testid="bil-readonly">
        {items.map(([label, val, type]) => (
          <div key={label} className="rounded-md border border-border p-2">
            <div className="text-[11px] font-semibold text-muted-foreground">{label}</div>
            <div className="text-sm font-semibold">{type === "node" ? val : (val == null || val === "" ? "—" : (type === "plain" ? val : eur(val)))}</div>
          </div>
        ))}
      </div>
      {p.bilancio?.updated_at && <p className="mt-2 text-[11px] text-muted-foreground">Acquisito il {itDate(p.bilancio.updated_at)}{p.bilancio.filename ? ` dal PDF «${p.bilancio.filename}»` : ""}</p>}
    </Card>
  );
}
